import { ContextProviderNode } from '@decisions/server/DecisionsFramework/Design/Properties/ContextProviderNode';
import { IActionLinkItems } from 'components/ActionLinks/ActionLinkModels';
import { IPropertyChild } from 'components/property-editors/utils/ProviderNodeUtils';
import { InputMappingConstants } from 'components/property-editors/MappingConstants';

export function getEditorValues(child: ContextProviderNode): string[] {
  const configValues = getEditorConfigValues(child);
  const values = configValues.find(value => value.Name === 'values');
  return (values && (values.OutputValue as string[])) || [];
}

export function getEditorValueIndex(value: string, child: ContextProviderNode) {
  return getEditorValues(child).indexOf(value);
}

export function getMappingEditorValues(child: ContextProviderNode): IActionLinkItems {
  const allValues = getEditorValues(child);
  const selected = getMappingValue(child)?.OutputValue;
  const current = allValues
    .filter(value => !value.includes(InputMappingConstants.CONVERTER_FLOW_CATEGORY_SEPARATOR) && value !== selected)
    .map(value => ({
      label: value === InputMappingConstants.MAPPING_UNKNOWN ? 'Clear' : value,
      value,
    }));

  const nested = getNestedValues(allValues);
  return { current, nested };
}

function getNestedValues(values: string[]) {
  const groupings = new Map<string, IActionLinkItems>();
  // this currently only handles one level of recursion.
  values
    .filter(value => value.includes(InputMappingConstants.CONVERTER_FLOW_CATEGORY_SEPARATOR))
    .forEach(value => {
      const groupKey = value.slice(0, value.indexOf('|'));
      value = value.slice(value.lastIndexOf('|') + 1);
      const label = value.slice(value.lastIndexOf('+') + 1);
      const entry = { label, value };
      const groupValue = groupings.get(groupKey) || { current: [] };
      groupValue.current && groupValue.current.push(entry);
      groupings.set(groupKey, groupValue);
    });

  return groupings;
}

export function getEditorConfigValues(child: ContextProviderNode) {
  return child && Array.isArray(child.EditorConfigurationValues) ? child.EditorConfigurationValues : [];
}

/**
 * Get a configuration value by name
 *
 * @param child whose configuration to search
 * @param name "Name" of data-pair whose "OutputValue" to return
 * @return matching OutputValue or undefined
 */
export function getEditorConfigValue<T = any>(child: ContextProviderNode, name: string): T {
  const pair = getEditorConfigValues(child).find(value => value.Name === name);
  return pair?.OutputValue;
}

export function isMappingUnknown(child: ContextProviderNode) {
  const value = getMappingValue(child);
  return value && value.OutputValue === 'Unknown';
}

export function getMappingValue(child: ContextProviderNode) {
  const values = getEditorConfigValues(child);
  return values.find(value => value.Name === 'mappingvalue');
}

export function getEditorConfigDisplayName(child: ContextProviderNode) {
  return getEditorConfigValue(child, 'type.displayname');
}

export function getEditorListData<T = any>(child: ContextProviderNode): T {
  return getEditorConfigValue(child, 'EditorListData');
}

export function showLabelAtTop(child: ContextProviderNode): boolean {
  return getEditorConfigValue(child, 'LabelAtTop');
}

export function getEditorSettings<K>(child: IPropertyChild): K {
  let settings = getEditorConfigValue(child, 'Settings');

  if (typeof settings === 'string') {
    settings = JSON.parse(settings);
  }
  return settings;
}
